import {useId} from 'react';
import cx from 'classnames';

import {Text} from '#component/base/Text';
import {sprinkles} from '#style/sprinkles';
import {theme} from '#theme';

import {RadialProgressProps} from './RadialProgress.types';

import * as css from './RadialProgress.css';

export const RadialProgress = ({
  className,
  stroke = 6,
  value = 0,
  maxValue = 100,
  label,
  ...otherProps
}: RadialProgressProps) => {
  const radius = 60;
  const diameter = radius * 2;
  const normalizedRadius = radius - stroke / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (value / maxValue) * circumference;
  const maskId = useId();

  return (
    <div
      aria-valuenow={value}
      aria-valuemax={maxValue}
      className={cx(css.root, className)}
      role="progressbar"
      {...otherProps}>
      {
        <Text
          aria-hidden
          color="deepBurgundy"
          className={sprinkles({
            content: 'center',
            flex: 'flex',
            inset: 0,
            items: 'center',
            pos: 'absolute',
          })}
          variant="heading1">
          {label || value}
        </Text>
      }

      <svg aria-hidden viewBox={`0 0 ${diameter} ${diameter}`}>
        <defs>
          <mask id={maskId}>
            <circle cx={radius} cy={radius} fill="white" r={normalizedRadius + stroke / 2} />
            <circle cx={radius} cy={radius} fill="black" r={normalizedRadius - stroke / 2} />
          </mask>
        </defs>

        <g style={{mask: `url(#${maskId})`}}>
          <foreignObject height={diameter} width={diameter} x="0" y="0">
            <div className={css.bg} />
          </foreignObject>

          <g className={css.progress}>
            <rect fill="white" x="0" y="0" width={diameter} height={diameter} />

            <circle
              cx={radius}
              cy={radius}
              className={css.fill}
              fill="transparent"
              r={normalizedRadius}
              stroke="black"
              strokeDasharray={circumference + ' ' + circumference}
              strokeWidth={stroke}
              style={{strokeDashoffset}}
            />
          </g>

          <circle
            className={css.track}
            cx={radius}
            cy={radius}
            fill="transparent"
            r={normalizedRadius}
            stroke={theme.colors.moonMist}
            strokeWidth={stroke}
          />
        </g>
      </svg>
    </div>
  );
};
